import type { MouseEvent } from 'react';
import { Fragment, createElement, useMemo } from 'react';

import type { IconList } from '@snowit/assets/index';
import type {
  A11y,
  ButtonIconColor,
  ButtonIconSize,
  ButtonIconTheme,
  Classnames,
} from '@snowit/icetypes';
import type { ButtonClick } from '@snowit/icetypes/events';

import { COLOR } from '@snowit/icetypes';
import testId from '@snowit/utils/testId';

import Icon from '../../Icon';
import LoaderSpinner from '../../Loaders/LoaderSpinner';
import Typo from '../../Typography/Typo';

import cx from 'classnames';
import s from './ButtonIcon.module.scss';

export interface IButtonIcon {
  theme?: ButtonIconTheme;
  rounded?: boolean;
  type?: string;
  icon?: IconList;
  label?: string;
  name?: string;
  value?: any;
  size?: ButtonIconSize;
  color?: ButtonIconColor | string;
  iconColor?: string;
  notHover?: boolean;
  loading?: boolean;
  disabled?: boolean;
  href?: string;
  onClick?: (params: ButtonClick) => void;
  id?: string;
  classNames?: Classnames<'button' | 'wrapIcon' | 'icon'>;
  ref?: any;
  a11y?: A11y;
  /** Jest/Cypress test string */
  test?: string;
}

const ButtonIcon = (props: IButtonIcon) => {
  const {
    theme = 'ghost',
    rounded,
    type,
    icon,
    label,
    name,
    value,
    size = 'tr',
    color = 'text',
    iconColor: _iconColor,
    notHover,
    loading,
    disabled,
    href,
    onClick,
    ref,
    id,
    classNames,
    a11y,
    test,
  } = props;

  const _color = COLOR.includes(color as ButtonIconColor) ? `var(--c-${color})` : color;

  const iconColor = useMemo(() => {
    if (_iconColor) return _iconColor;
    if (theme !== 'fill') return _color;
    if (color === 'primary') return 'var(--c-primary-contrast)';
    return 'var(--c-white)';
  }, [_iconColor, color, _color, theme]);

  const handleClick = (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (disabled || loading) return;
    if (typeof onClick === 'function') {
      onClick({ event, name, id, value });
    }
  };

  const renderIcon = () => {
    return (
      <div className={cx(s.wrapIcon, classNames?.wrapIcon)}>
        {loading ? (
          <LoaderSpinner className={s.loader} />
        ) : (
          <Icon
            className={cx(s.icon, classNames?.icon)}
            name={icon}
            a11y={{ label: a11y?.label || label, hidden: true }}
          />
        )}
      </div>
    );
  };

  const renderLabel = () => {
    if (!label) return null;
    return <Typo text={label} tag={'span'} size={'sm'} color={'text-l'} align={'center'} />;
  };

  const ButtonContent = (
    <Fragment>
      {renderIcon()}
      {renderLabel()}
    </Fragment>
  );

  const isButton = () => !(href && !onClick);

  const tag = isButton() ? 'button' : 'a';

  const buttonProps = {
    id,
    ...testId(test),
    className: cx(s.button, classNames?.button, {
      [s[theme]]: true,
      [s.rounded]: rounded,
      [s.hasLabel]: label,
      [s.notHover]: notHover || disabled,
      [s.disabled]: disabled || loading,
    }),
    style: {
      '--l-h': `var(--s-${size})`,
      '--l-color-bg': _color,
      '--l-color-icon': iconColor,
    },
    disabled: disabled || loading,
    role: a11y?.role,
    'aria-label': a11y?.label,
    'aria-hidden': a11y?.hidden,
    'aria-expanded': a11y?.expanded,
    'aria-controls': a11y?.controls,
    'aria-haspopup': a11y?.haspopup,
    'data-loading': loading || undefined,
    ref,
    ...(!isButton() && {
      rel: 'noreferrer',
      target: '_blank',
      href,
    }),
    ...(isButton() && {
      onClick: handleClick,
      value,
      type,
    }),
  };

  return createElement(tag, buttonProps, ButtonContent);
};

export default ButtonIcon;
